
//The whole UI is a container

import { CONST } from "../CONST";
import { MenuScene } from "../scenes/MenuScene";
import { ProgressiveLoadScene } from "../scenes/ProgressiveLoadScene";

interface MinigameButton {
    scene: string,
    defaultImage: string,
    selectedImage: string,
    position: number,
    isMinigame: boolean,
    isArcade: boolean,
}

const animConfig = {
    openOnStart: false,
    duration: 300,
    easeIn: "Quad.easeOut",
    easeOut: "Quad.easeIn",
    closedPosition: {x: 426.5, y: -240},
    openPosition: {x: 426.5, y: 257},
}

const buttonConfig = {
    grid: {
        center:{x: 0, y: 23}, 
        yOffset: 72,
        xOffset: 215,
    },

    newOffset: {
      x: 85,
      y: -59,  
    },

    highscoreOffset: {
        x: -66,
        y: 55,
        fontSize: 25,
    },

    minigames: [
        {
           scene: CONST.SCENES.INSTRUMENT,
           defaultImage: CONST.ARCADE_SCREEN.INSTRUMENT,
           selectedImage: CONST.ARCADE_SCREEN.INSTRUMENT_SELECTED,
           isMinigame: true,
           isArcade: true,
           position: 3, 
           rcPosition: 0, //position of index in remote config
        },
        {
            scene: CONST.SCENES.BURGER,
            defaultImage: CONST.ARCADE_SCREEN.BURGER,
            selectedImage: CONST.ARCADE_SCREEN.BURGER_SELECTED,
            isMinigame: true,
            isArcade: true,
            position: 0, 
            rcPosition: 1, //position of index in remote config
         },
         {
            scene: CONST.SCENES.DISTRACTION,
            defaultImage: CONST.ARCADE_SCREEN.DISTRACTION,
            selectedImage: CONST.ARCADE_SCREEN.DISTRACTION_SELECTED,
            isMinigame: true,
            isArcade: true,
            position: 1, 
            rcPosition: 2, //position of index in remote config
         },
         {
            scene: CONST.SCENES.GRID_FILL,
            defaultImage: CONST.ARCADE_SCREEN.GRIDFILL,
            selectedImage: CONST.ARCADE_SCREEN.GRIDFILL_SELECTED,
            isMinigame: true,
            isArcade: true,
            position: 2, 
            rcPosition: 3, //position of index in remote config
         },
         {
            scene: CONST.SCENES.BIRTH,
            defaultImage: CONST.ARCADE_SCREEN.MAIN_GAME,
            selectedImage: CONST.ARCADE_SCREEN.MAIN_GAME_SELECTED,
            isMinigame: true,
            isArcade: false,
            position: 4, 
         },
         {
            scene: "",
            defaultImage: CONST.ARCADE_SCREEN.NEW_LEVELS,
            selectedImage: "",
            isMinigame: false,
            isArcade: false,
            position: 5, 
         }
    ],
}

export class ArcadePopup {
    private scene: MenuScene;
    private cont: Phaser.GameObjects.Container;
    private darkBg !: Phaser.GameObjects.Image;

    private moveTween !: Phaser.Tweens.Tween;
    private fadeTween !: Phaser.Tweens.Tween;

    private progressiveLoadScene !: ProgressiveLoadScene;

    private hasNewGames : boolean = false;

    private arcadeButtonPositions = "3012"; //From remote config


    constructor(scene : MenuScene){
        this.scene = scene;
        this.progressiveLoadScene = this.scene.registry.get(CONST.SCENES.PROGRESSIVE_LOAD);

        this.darkBg = scene.add.image(0, 0, CONST.ARCADE_SCREEN.DARK_LAYER).setOrigin(0, 0).setDepth(49).setAlpha(0);

        this.cont = scene.add.container(this.scene.game.renderer.width/2, this.scene.game.renderer.height/2 + 15).setDepth(50);

        this.getRemoteConfig();

        if (animConfig.openOnStart){
            this.cont.setPosition(animConfig.openPosition.x, animConfig.openPosition.y);
        } else {
            this.cont.setPosition(animConfig.closedPosition.x, animConfig.closedPosition.y);
        }

        this.buildUi();

    }

    private getRemoteConfig(){
        this.arcadeButtonPositions = this.scene.remoteConf.getConfig("gO");
    }

    public open(){
        this.prioritizeLoading();
        this.animate(true);
    }

    private prioritizeLoading(){
        if (!this.progressiveLoadScene) return;
        this.progressiveLoadScene.prioritizeChunksWithTag(CONST.TAGS.ARCADE);
    }

    private close(){
        this.animate(false);
        this.scene.setButtonsInteractive(true);
    }

    private animate(animateIn: boolean){
        if (this.moveTween){
            this.moveTween.stop();
        }

        this.moveTween = this.scene.tweens.add({
            targets: this.cont,
            x: animateIn ? animConfig.openPosition.x : animConfig.closedPosition.x,
            y: animateIn ? animConfig.openPosition.y : animConfig.closedPosition.y,
            duration: animConfig.duration,
            ease: animateIn ? animConfig.easeIn : animConfig.easeOut,
        });

        if (this.fadeTween){
            this.fadeTween.stop();
        }

        this.fadeTween = this.scene.tweens.add({
            targets: this.darkBg,
            alpha: animateIn ? 1 : 0,
            duration: animConfig.duration,
            ease: animateIn ? animConfig.easeIn : animConfig.easeOut,
        });    
    }

    private buildUi(){
        let bg = this.scene.add.image(0, 0, CONST.ARCADE_SCREEN.BACKGROUND);
       
        let homeButton = this.scene.add.image(-318, -175, CONST.ARCADE_SCREEN.HOME_BUTTON);
        homeButton.setInteractive({useHandCursor: true, pixelPerfect: true});
        homeButton.on("pointerup", ()=> {
            this.close();
        });
        this.cont.add([bg, homeButton]);

        buttonConfig.minigames.forEach(minigame => this.buildMinigameButton(minigame, this.cont))
    }

    private buildMinigameButton(button : MinigameButton, cont: Phaser.GameObjects.Container){
        let bottonPosNumber = button.isArcade ? parseInt(this.arcadeButtonPositions[button.rcPosition]) : button.position; 
        let buttonPos = this.getButtonPosition(bottonPosNumber);
        let defaultImage = this.scene.add.image(buttonPos.x, buttonPos.y, button.defaultImage);
        defaultImage.setInteractive({useHandCursor: true, pixelPerfect: true});

        cont.add(defaultImage);

        let newTag = this.scene.add.image(buttonPos.x + buttonConfig.newOffset.x, 
            buttonPos.y + buttonConfig.newOffset.y, 
            CONST.ARCADE_SCREEN.NEW)
            .setAngle(-5);

        if (button.isMinigame){
            let selectedImage = this.scene.add.image(buttonPos.x, buttonPos.y, button.selectedImage).setAlpha(0);
            defaultImage.on("pointerover", () => {
                selectedImage.setAlpha(1);

                //Wiggle
                this.shakeTag(newTag, -5);
            });
            defaultImage.on("pointerout", () => {
                selectedImage.setAlpha(0);
            });
            cont.add(selectedImage);

            let highscore = this.getHighscore(button.scene);
            let highscoreText = this.scene.add.bitmapText(buttonPos.x + buttonConfig.highscoreOffset.x,
                                                             buttonPos.y + buttonConfig.highscoreOffset.y,
                                                              CONST.FONT.ATLAS,
                                                              highscore.toString(),
                                                              buttonConfig.highscoreOffset.fontSize  / (highscore.toString().length > 5 ? highscore.toString().length / 5 : 1))
                                    .setOrigin(0.5).setCenterAlign();
            if (!button.isArcade){
                highscoreText.text = "";
            }
        
            cont.add(highscoreText);

            defaultImage.on("pointerup", () => {
                this.handlePlayButtonClick(button.scene);
            });
        } else {
            defaultImage.on("pointerup", () => {
                this.shakeTag(defaultImage, 0);
            });
        }

        cont.add(newTag);

        if (!button.isArcade || this.hasBeenPlayed(button.scene)){
            newTag.setAlpha(0)
        } else {
            this.hasNewGames = true;
        }
    }

    private shakeTag(tag: Phaser.GameObjects.Image, defaultAngle: number){
        tag.setAngle(defaultAngle);
        this.scene.tweens.add({
            targets: tag,
            angle: "+=5",
            duration: 500,
            ease: (t: number) => {return this.wiggle(t, 1, 1)},
            onComplete: () => {tag.setAngle(defaultAngle)}
        });
    }

    private getButtonPosition(position: number){
       // 0 , 1 , 2
       // 3 , 4 , 5
       let y = buttonConfig.grid.center.y + (position >= 3 ? buttonConfig.grid.yOffset : -buttonConfig.grid.yOffset);
       let x = buttonConfig.grid.center.x + (position % 3 == 0 ? -buttonConfig.grid.xOffset : position % 3 == 1 ? 0 : buttonConfig.grid.xOffset);
       return {x: x, y: y};
    }

    private handlePlayButtonClick(minigame: string) {
        if (this.progressiveLoadScene.isSceneLoaded(minigame)){
            this.scene.scene.start(minigame);
        } else {
            this.scene.scene.start(CONST.SCENES.ARCADE_MINIGAME_LOADING, {next: minigame});
        }         
    }

    private hasBeenPlayed(scene: string){
        if (this.scene.storagePlugin.getItem(scene+"highscore")) return true;

        return false;
    }

    protected getHighscore(scene:string) : number{
        var highscore = this.scene.storagePlugin.getItem(scene + "highscore");
        if (highscore){
            return parseInt(highscore);
        } else {
            return 0;
        }
     }

    private wiggle(aProgress: number, aPeriod1: number, aPeriod2: number): number {
        var current1: number = aProgress * Math.PI * 2 * aPeriod1;
        var current2: number = aProgress * (Math.PI * 2 * aPeriod2 + Math.PI / 2);

        return Math.sin(current1) * Math.cos(current2);
    }

    public hasNewArcadeGames() : boolean {
        return this.hasNewGames;
    }

}